import { Box } from "@mui/material";
import { useCallback, useContext, useRef, useState } from "react";
import { AppContext } from "../../common/AppContext";
import { Dialog } from "../../components/dialogs/Dialog";
import StandardLibraryCheck from "../../components/StandardLibraryCheck";
import { UPLOAD_JOB_DESCRIPTION_STEP } from "../../constants";
import { IIndustryStandardMatches } from "../../interfaces/Job";
import IndustryStandardMatches, { IndustryStandardMatchesProps } from "./IndustryStandardMatches";
import UploadJobDescriptionFile, {
  UploadJobDescriptionFileProps
} from "./UploadJobDescriptionFile";
import { useLocale } from "../../providers/LocaleProvider";

const StepContentMap = new Map<
  UPLOAD_JOB_DESCRIPTION_STEP,
  | React.ComponentType<UploadJobDescriptionFileProps>
  | React.ComponentType<IndustryStandardMatchesProps>
>([
  [UPLOAD_JOB_DESCRIPTION_STEP.UPLOAD_FILE, UploadJobDescriptionFile],
  [UPLOAD_JOB_DESCRIPTION_STEP.INDUSTRY_STANDARD_MATCHES, IndustryStandardMatches]
]);

export const ImportJobDescriptionDialog = ({ theme, onClose, open }: any) => {
  const [activeStep, setActiveStep] = useState(UPLOAD_JOB_DESCRIPTION_STEP.UPLOAD_FILE);
  const { organization } = useContext(AppContext);
  const { translate } = useLocale();

  const industryStandardMatchesRef = useRef<IIndustryStandardMatches>({
    jobDefinitions: [],
    tasks: [],
    competencies: []
  });

  const onUploaded = useCallback((data: IIndustryStandardMatches) => {
    industryStandardMatchesRef.current = data;
    setActiveStep(UPLOAD_JOB_DESCRIPTION_STEP.INDUSTRY_STANDARD_MATCHES);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    industryStandardMatchesRef.current = {
      jobDefinitions: [],
      tasks: [],
      competencies: []
    };
  }, [onClose]);

  const renderStepContent = useCallback(() => {
    const StepContentComponent = StepContentMap.get(activeStep);
    if (!StepContentComponent) {
      return null;
    }

    return (
      <StepContentComponent
        theme={theme}
        jobs={industryStandardMatchesRef.current.jobDefinitions}
        tasks={industryStandardMatchesRef.current.tasks}
        competencies={industryStandardMatchesRef.current.competencies}
        onClose={handleClose}
        onUploaded={onUploaded}
      />
    );
  }, [activeStep, theme, onUploaded, handleClose]);

  return (
    <Dialog
      open={open}
      theme={theme}
      onClose={handleClose}
      fullWidth
      maxWidth='md'
      title={translate("settings.uploadJobDescription")}
      disabled
      {...(!organization?.enableStandardsLibrary && {
        actions: [
          {
            label: translate("common.close"),
            onClick: handleClose
          }
        ]
      })}
    >
      <StandardLibraryCheck>
        <Box>{renderStepContent()}</Box>
      </StandardLibraryCheck>
    </Dialog>
  );
};
